import React, { Component } from 'react';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import { push } from 'modules/history';
import InputSearch from 'components/InputSearch';
import H4 from 'components/H4';
import ButtonText from 'components/ButtonText';
import PaginationButtons from 'components/PaginationButtons';
import Table, { THeader, TableTitle, TRow, TableItem, TBody } from 'components/Table';
import Dropdown from 'components/Dropdown';
import AlertConfirm from 'components/AlertConfirm';
import ListEmpty from 'components/ListEmpty';
import Loader from 'components/Loader';
import PropTypes from 'prop-types';
import { listHeaderPrimaryButtonStyles } from 'constants/listHeaderButtons';

import AlertBreakdownTooltip from './metrics/AlertBreakdownTooltip';
import BrandListMetrics from './metrics/BrandListMetrics';
import InfoTooltip from './metrics/InfoTooltip';
import XOverY from './metrics/XOverY';

const Container = styled.div``;

const Content = styled.div`
  padding: 24px;
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const TableContainer = styled.div`
  padding-bottom: 50px;
  margin-bottom: 50px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 250px;
  right: 0;
  padding: 0 24px 25px 24px;
  background-color: ${Colors.white};
  box-sizing: border-box;
  @media (max-width: 700px) {
    left: 60px;
  }
`;

const HeaderWithInfo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const INSTALLED_TOOLTIP =
  "Devices that are live in the field / physical locations they're installed at. Each physical location may have multiple devices (Softener, RO, Water Filter).";
const PENDING_TOOLTIP =
  'Devices created in Aquarian but not yet installed / physical locations waiting on at least one install.';
const ALERT_TOOLTIP = 'Physical locations with one or more pending alerts.';

export default class BrandListView extends Component {
  state = {
    dropdownOpenId: null,
    brandSelected: null,
    isOpenAlert: false,
  };

  switchDropdown = key => {
    const { dropdownOpenId } = this.state;
    const id = dropdownOpenId !== key ? key : null;

    this.setState({ dropdownOpenId: id });
  };

  switchAlert = (brandSelected = null) => {
    const { isOpenAlert } = this.state;

    this.setState({ isOpenAlert: !isOpenAlert, brandSelected });
  };

  editBrand = ({ id }) => {
    push(`/brands/${id}/edit`);
  };

  remove = () => {
    const { removeBrand } = this.props;
    const {
      brandSelected: { id },
    } = this.state;

    removeBrand(id);
    this.switchAlert();
  };

  renderItem = (item, key) => {
    const metrics = (this.props.metricsByBrand && this.props.metricsByBrand[item.id]) || null;
    const installedDevices = metrics ? metrics.devices_installed : 0;
    const installedSites = metrics ? metrics.sites_with_installed : 0;
    const pendingDevices = metrics ? metrics.devices_pending : 0;
    const pendingSites = metrics ? metrics.sites_with_pending : 0;
    const sitesInAlert = metrics ? metrics.sites_in_alert : 0;
    const alertBreakdown = metrics ? metrics.alert_breakdown || [] : [];

    return (
      <TRow key={key}>
        <TableItem width="20%">{item.name}</TableItem>
        <TableItem width="20%">
          {item.contact_first_name && item.contact_last_name
            ? `${item.contact_first_name} ${item.contact_last_name}`
            : item.brandAdmin
            ? `${item.brandAdmin.first_name} ${item.brandAdmin.last_name}`
            : '-'}
        </TableItem>
        <TableItem width="14%">{item.amount_accounts}</TableItem>
        <TableItem width="14%" isComponent>
          <XOverY devices={installedDevices} sites={installedSites} size={14} />
        </TableItem>
        <TableItem width="14%" isComponent>
          <XOverY devices={pendingDevices} sites={pendingSites} size={14} />
        </TableItem>
        <TableItem width="10%" isComponent>
          <AlertBreakdownTooltip total={sitesInAlert} breakdown={alertBreakdown} size={14} />
        </TableItem>
        <TableItem width="8%" isComponent>
          <Dropdown
            icon="ellipsis-v"
            options={[
              {
                title: 'Edit',
                icon: 'pencil-alt',
                color: Colors.green,
                action: () => this.editBrand(item),
              },
              {
                title: 'Remove',
                icon: 'trash',
                color: Colors.red,
                action: () => this.switchAlert(item),
              },
            ]}
            isDropdownOpen={this.state.dropdownOpenId === key}
            onClickDropdownButton={() => this.switchDropdown(key)}
          />
        </TableItem>
      </TRow>
    );
  };

  searchOnChange = search => {
    this.setState({ search }, () => {
      const { fetchBrands } = this.props;
      const { search } = this.state;

      fetchBrands({ search });
    });
  };

  renderAlertConfirm = () => {
    const { isOpenAlert, brandSelected } = this.state;

    return (
      <AlertConfirm
        title="Remove brand"
        onCancel={() => this.switchAlert()}
        onSuccess={this.remove}
        successText="Remove"
        isVisible={isOpenAlert}
      >
        <H4 style={{ textAlign: 'center' }}>
          {`Are you sure that want remove the "${brandSelected ? brandSelected.name : ''}" brand`}
        </H4>
      </AlertConfirm>
    );
  };

  render() {
    const { list, pages, currentPage, loading, fetchBrands } = this.props;
    const { search } = this.state;

    return (
      <Container>
        <BrandListMetrics
          actions={
            <ButtonText
              theme="primary"
              text="New"
              icon="plus"
              customStyles={listHeaderPrimaryButtonStyles}
              onClick={() => push('/brands/create')}
            />
          }
        >
          <SearchContainer>
            <InputSearch onChange={this.searchOnChange} value={search} name="search" />
          </SearchContainer>
        </BrandListMetrics>
        <Content>
          <TableContainer>
            <Table>
              <THeader>
                <TableTitle width="20%">Name</TableTitle>
                <TableTitle width="20%">Point of Contact</TableTitle>
                <TableTitle width="14%">Accounts</TableTitle>
                <TableTitle width="14%">
                  <HeaderWithInfo>
                    Installed
                    <InfoTooltip content={INSTALLED_TOOLTIP} />
                  </HeaderWithInfo>
                </TableTitle>
                <TableTitle width="14%">
                  <HeaderWithInfo>
                    Pending
                    <InfoTooltip content={PENDING_TOOLTIP} />
                  </HeaderWithInfo>
                </TableTitle>
                <TableTitle width="10%">
                  <HeaderWithInfo>
                    In Alert
                    <InfoTooltip content={ALERT_TOOLTIP} />
                  </HeaderWithInfo>
                </TableTitle>
                <TableTitle width="8%">&nbsp;</TableTitle>
              </THeader>
              <TBody>
                {loading ? (
                  <Loader size={100} block />
                ) : (
                  list.map((item, key) => this.renderItem(item, key))
                )}
              </TBody>
            </Table>
            {(!list || !list.length) && !loading && (
              <ListEmpty message="There are currently no brands" />
            )}
          </TableContainer>
        </Content>
        <Footer>
          <PaginationButtons
            currentPage={currentPage}
            changePage={page => fetchBrands({ page })}
            totalPages={pages}
          />
        </Footer>
        {this.renderAlertConfirm()}
      </Container>
    );
  }
}

BrandListView.propTypes = {
  currentPage: PropTypes.number,
  fetchBrands: PropTypes.func,
  list: PropTypes.array,
  loading: PropTypes.bool,
  metricsByBrand: PropTypes.object,
  pages: PropTypes.number,
  removeBrand: PropTypes.func,
};
